import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  limit,
} from "firebase/firestore";
import { Helmet } from "react-helmet";

// --- IMPORTANT: PLEASE VERIFY THESE IMPORT PATHS CAREFULLY ---
// The errors "Could not resolve..." mean your project's file structure
// does NOT match the paths specified below.
// You MUST adjust these paths if your files are in different locations
// relative to where this 'Product.js' file is saved.
//
// Example: If 'Product.js' is in 'src/pages/', then:
// - '../config/Firebase' means 'src/config/Firebase.js'
// - '../components/ProductShowcase' means 'src/components/ProductShowcase.js'
// - '../assets/images/im6.png' means 'src/assets/images/im6.png'

// 1. Firebase Configuration File:
// ✅ CHECK THIS PATH: Is 'Firebase.js' (or Firebase.ts) located inside a folder named 'config',
//    which is one level up from the current file?
import FirebaseApp from "../config/Firebase";

// 2. Component Files:
// ✅ CHECK THESE PATHS: Are these components located inside a folder named 'components',
//    which is one level up from the current file?
import ProductShowcase from "../components/ProductShowcase";
import TypesProducts from "../components/TypesProducts";
import Pcontainer from "../components/pcontainer"; // Note: lowercase 'p' for pcontainer.js
import Footer from "../components/Footer";

// 3. Image Files:
// ✅ CHECK THESE PATHS: Are your images located inside 'assets/images' folder,
//    which is two levels up from the current file?
//    Also, pay close attention to the EXACT filename and extension (case-sensitive!).
import im6 from "../assets/images/im6.png";
import im7 from "../assets/images/im7.png";
import im8 from "../assets/images/im8.png";
import im9 from "../assets/images/im9.jpeg"; // Is it '.jpeg' or '.jpg'? Or '.png'?
import im10 from "../assets/images/im10.jpeg"; // Is it '.jpeg' or '.jpg'? Or '.png'?
import im11 from "../assets/images/im11.png";
import im12 from "../assets/images/im12.png";
import im13 from "../assets/images/im13.png";
import im14 from "../assets/images/im14.jpg";
import im15 from "../assets/images/im15.png";
import im16 from "../assets/images/im16.png";
import im17 from "../assets/images/im17.png";
import im33 from "../assets/images/im33.jpg";
// --- END IMPORTANT: PATH VERIFICATION ---

// Data arrays (unchanged - these don't depend on file paths)
const brand = [
  im6,
  im7,
  im8,
  im9,
  im10,
  im11,
  im12,
  im13,
  im14,
  im15,
  im16,
  im17,
];
const brands = [
  "Zeiss",
  "Essilor",
  "Rodenstock",
  "Vision Rx",
  "Nova",
  "Kodak",
  "Vogue",
  "Tom Ford",
  "Ray-Ban",
  "Carrera",
  "Police",
  "Boss",
  "Mont Blanc",
];
const collections = [
  "DesignerMetal&MetalFrame",
  "DesignerMetal_MetalFrame",
  "Accessories",
  "FrameLess",
  "FullFrame",
  "HalfFrame",
  "Kids-Frame",
  "Kids-Sunglass",
  "LDB",
  "SafetyGlassess",
  "Sunglassess",
  "Types",
];
const images = [im9, im10, im33]; // Not directly used in the current Product component, but kept for context

// Debounce function to limit how often a function is called
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export default function App() {
  // Renamed from Product to App for default export, common React practice
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const db = getFirestore(FirebaseApp);

  // Load user from localStorage on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) setUser(JSON.parse(savedUser));
  }, []);

  // Utility function to validate email format
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Utility function to validate phone number format
  const validatePhone = (phone) => {
    // Allows for optional '+' at the beginning, followed by 7 to 15 digits
    const regex = /^\+?\d{7,15}$/;
    return regex.test(phone);
  };

  // Function to handle product search in Firestore
  const handleSearch = async () => {
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    try {
      let results = [];
      const searchTermLower = searchTerm.toLowerCase();

      // Iterate through predefined collections to search for products
      for (const collectionName of collections) {
        const colRef = collection(db, collectionName);
        // Limit to 100 documents per collection for performance.
        // For larger datasets, consider backend search or more advanced indexing.
        const q = query(colRef, limit(100));
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((doc) => {
          const productData = {
            id: doc.id,
            ...doc.data(),
            Category: collectionName,
          };
          const { ProductID, Brand, Shape, Name, FrameColor } = productData;

          // Check if any relevant product field includes the search term
          if (
            (ProductID && ProductID.toLowerCase().includes(searchTermLower)) ||
            (Brand && Brand.toLowerCase().includes(searchTermLower)) ||
            (Shape && Shape.toLowerCase().includes(searchTermLower)) ||
            (Name && Name.toLowerCase().includes(searchTermLower)) ||
            (FrameColor && FrameColor.toLowerCase().includes(searchTermLower))
          ) {
            // Prevent duplicate products if they appear in multiple collections (unlikely but good safeguard)
            if (
              !results.some(
                (result) =>
                  result.id === productData.id &&
                  result.Category === productData.Category
              )
            ) {
              results.push(productData);
            }
          }
        });
      }

      setSearchResults(results);
    } catch (error) {
      console.error("Error fetching product details:", error);
      setSearchResults([]);
    }
  };

  // Debounced search effect to prevent excessive API calls while typing
  const debouncedSearch = debounce(handleSearch, 300);
  useEffect(() => {
    debouncedSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // Re-run debounce on searchTerm change

  // Navigate to product details page
  const handleViewProduct = (product) => {
    navigate(`/product-details`, { state: product });
  };

  // Function to send welcome email via the backend API
  const sendWelcomeEmail = async (email, username, phone) => {
    console.log("Attempting to send welcome email to:", {
      email,
      username,
      phone,
    });
    try {
      // **Frontend API endpoint:** Ensure this URL is correct for your VPS
      const response = await fetch(
        "https://aramemail.outliersunited.com/send-new-user-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, username, phone }),
        }
      );

      // Get raw response text first for comprehensive error logging
      const text = await response.text();
      console.log(
        "Raw response from backend:",
        text,
        "Status:",
        response.status
      );

      // Check if the HTTP response status is not OK (e.g., 4xx, 5xx)
      if (!response.ok) {
        let errorMessage = `Failed to send welcome email: ${response.statusText} (${response.status})`;
        try {
          const errorJson = JSON.parse(text);
          errorMessage += ` - Backend Error: ${errorJson.error || JSON.stringify(errorJson)
            }`;
        } catch (parseError) {
          // If response is not valid JSON, append raw text to the error message
          errorMessage += ` - Response Body: ${text}`;
        }
        // Throw an error to be caught by the outer catch block
        throw new Error(errorMessage);
      }

      // If response is OK, try to parse it as JSON
      const result = JSON.parse(text);
      if (result.success) {
        alert("Welcome email sent successfully!");
      } else {
        // If 'success' is false in the JSON, throw an error with the backend's error message
        throw new Error(result.error || "Unknown error sending email");
      }
    } catch (error) {
      // This catch block handles network errors (e.g., TypeError: Failed to fetch)
      // or custom errors thrown from the above logic.
      // Reminder: The "multiple Access-Control-Allow-Origin headers" error, which caused
      // "Failed to fetch" in your frontend, was resolved by commenting out
      // `app.use(cors(corsOptions));` in your Node.js backend server.
      // This ensures Nginx is the sole provider of the CORS headers.
      console.error("Error sending welcome email:", error);
      alert(
        `Failed to send welcome email: ${error.message}. Please check console for details.`
      );
    }
  };

  // Handles user login or signup, interacts with Firebase and sends welcome email
  const handleLoginSignup = async () => {
    // Client-side validation for input fields
    if (!email || !username || !phone) {
      alert("Please enter email, username, and phone number.");
      return;
    }
    if (!validateEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }
    if (!validatePhone(phone)) {
      alert(
        "Please enter a valid phone number (digits only, at least 7 characters)."
      );
      return;
    }

    try {
      // Check if user already exists in Firebase
      const userDocRef = doc(db, "UID", email);
      const userSnap = await getDoc(userDocRef);

      if (!userSnap.exists()) {
        // If user does not exist, create new user document in Firebase
        await setDoc(userDocRef, { email, username, phone });
        // Send welcome email only for newly registered users
        await sendWelcomeEmail(email, username, phone);
        alert("Account created successfully and welcome email sent!");
      } else {
        alert("User with this email already exists. Logging in.");
        // If user exists, you might want to fetch their existing username/phone
        // or just proceed with logging them in if the purpose is just to set 'user' state
      }

      // Store user data in local state and localStorage
      const userData = { email, username, phone };
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      setShowPopup(false); // Close the login/signup popup
    } catch (error) {
      console.error("Error during login/signup:", error);
      alert(`Error during signup: ${error.message}. Please try again.`);
    }
  };

  // Handles user logout
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("user"); // Clear user data from localStorage
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>
          Aram Eye Care | Designer Glasses & Eyewear for Men, Women, Kids
        </title>
        <meta
          name="description"
          content="Shop men's glasses, women's glasses, kids' eyewear, sunglasses, lenses, and accessories at Aram Eye Care. Featuring Ray-Ban, Zeiss, Vogue, and more trusted brands."
        />
        <meta
          name="keywords"
          content="eyewear, glasses, sunglasses, men's glasses, women's glasses, kids eyewear, designer glasses, fashion glasses, blue light glasses, reading glasses, optical frames, prescription glasses, unisex eyewear, stylish glasses, luxury eyewear, rimless glasses, full frame glasses, half frame, branded glasses, Aram Eye Care, Ray-Ban, Zeiss, Vogue, Essilor, Rodenstock, Nova, Kodak, Tom Ford, Carrera, Police, optical store, eye clinic, vision care, power glasses, power glasses for eyes, spectacles for men, stylish spectacles for men, men’s spectacles, frames for men’s glasses, branded glasses for male, office wear specs for men, men’s prescription glasses, glasses for men’s face shape, stylish glasses for women, trendy glasses for women, designer glasses for women, women’s spectacles, women’s power glasses, glasses for round face women, fashion eyewear for ladies, frames for women’s glasses, glasses for kids, spectacles for children, kids prescription glasses, school glasses for kids, unbreakable frames for kids, flexible specs for children, stylish kids eyewear, baby glasses, eye protection glasses for kids, specs glasses, anti-glare spectacles, computer glasses, lightweight eyewear, affordable branded glasses"
        />
        <meta name="author" content="Aram Eye Care" />
        <link rel="canonical" href="https://www.arameyecare.com/home" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="Aram Eye Care – Stylish Glasses & Sunglasses for All Ages"
        />
        <meta
          property="og:description"
          content="Premium eyewear for men, women, and kids. Choose from top brands like Ray-Ban, Zeiss, and Vogue. Visit Aram Eye Care online or in-store."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.arameyecare.com/home" />
        <meta property="og:site_name" content="Aram Eye Care" />
        <meta
          property="og:image"
          content="https://www.arameyecare.com/og-image.jpg"
        />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Aram Eye Care – Designer Glasses & Sunglasses"
        />
        <meta
          name="twitter:description"
          content="Discover luxury eyewear and prescription glasses for men, women, and children. Shop trusted brands from Aram Eye Care."
        />
        <meta
          name="twitter:image"
          content="https://www.arameyecare.com/twitter-image.jpg"
        />
        <meta name="twitter:site" content="@arameyecare" />

        {/* Structured Data – Organization */}
        <script type="application/ld+json">
          {`
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Aram Eye Care",
        "url": "https://www.arameyecare.com",
        "logo": "https://www.arameyecare.com/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+911234567890",
          "contactType": "Customer Support",
          "areaServed": "IN",
          "availableLanguage": ["English", "Tamil"]
        },
        "sameAs": [
          "https://facebook.com/arameyecare",
          "https://instagram.com/arameyecare",
          "https://www.linkedin.com/company/arameyecare"
        ]
      }
    `}
        </script>

        {/* Structured Data – WebSite with SearchAction */}
        <script type="application/ld+json">
          {`
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Aram Eye Care",
        "url": "https://www.arameyecare.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.arameyecare.com/search?query={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    `}
        </script>

        {/* Structured Data – Local Business */}
        <script type="application/ld+json">
          {`
      {
        "@context": "https://schema.org",
        "@type": "Optician",
        "name": "Aram Eye Care",
        "image": "https://www.arameyecare.com/logo.png",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "123 Aram Street",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600001",
          "addressCountry": "IN"
        },
        "telephone": "+911234567890",
        "openingHours": "Mo-Sa 09:00-18:00",
        "url": "https://www.arameyecare.com"
      }
    `}
        </script>
      </Helmet>

      {/* Header Section */}
      <header className="bg-white shadow sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-4 flex flex-col md:flex-row items-center justify-between">
          <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-6 w-full md:w-auto">
            <h1 className="text-xl md:text-3xl font-bold text-gray-800">
              Aram Eye Care
            </h1>
            <div className="hidden md:flex space-x-4">
              <Link
                to="/home"
                className="text-gray-600 hover:text-blue-600 font-semibold"
              >
                Home
              </Link>
              <Link
                to="/blog"
                className="text-gray-600 hover:text-black font-semibold"
              >
                Blog
              </Link>
              <Link
                to="/about"
                className="text-gray-600 hover:text-black font-semibold"
              >
                About Us
              </Link>
            </div>
          </div>

          <div className="flex-1 my-2 md:my-0 flex justify-center w-full md:max-w-md">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search by Product ID..."
                className="w-full border border-gray-300 rounded-full py-1 md:py-2 px-4 text-sm md:text-base focus:outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={handleSearch}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white rounded-full px-3 py-1 md:px-4 md:py-2 text-sm md:text-base"
              >
                Search
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-4 w-full md:w-auto justify-end mt-2 md:mt-0">
            <div className="hidden md:flex space-x-4">
              <Link
                to="/wishlist"
                className="text-gray-600 hover:text-black font-semibold"
              >
                Wishlist
              </Link>
              <Link
                to="/UserOrder"
                className="text-gray-600 hover:text-black font-semibold"
              >
                Order
              </Link>
            </div>
            {user ? (
              <div className="flex items-center space-x-2">
                <span className="text-sm md:text-base text-gray-800 font-semibold">
                  {user.username}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-gray-900 text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-sm md:text-base"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPopup(true)}
                className="bg-gray-900 text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-sm md:text-base"
              >
                Login / Signup
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden bg-white border-t border-b shadow-sm">
          <div className="container mx-auto px-2 py-1 flex justify-around flex-wrap">
            <Link
              to="/home"
              className="text-xs px-1 py-1 hover:text-blue-600 font-semibold"
            >
              Home
            </Link>
            <Link
              to="/blog"
              className="text-xs px-1 py-1 hover:text-blue-600 font-semibold"
            >
              Blog
            </Link>
            <Link
              to="/about"
              className="text-xs px-1 py-1 hover:text-blue-600 font-semibold"
            >
              About
            </Link>
            <Link
              to="/wishlist"
              className="text-xs px-1 py-1 hover:text-blue-600 font-semibold"
            >
              Wishlist
            </Link>
            <Link
              to="/UserOrder"
              className="text-xs px-1 py-1 hover:text-blue-600 font-semibold"
            >
              Order
            </Link>
          </div>
        </nav>
      </header>

      {/* Login Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-4 w-11/12 max-w-xs">
            <h2 className="text-xl font-bold mb-3 text-center">
              Login / Signup
            </h2>
            <input
              type="email"
              placeholder="Email"
              className="w-full mb-2 p-2 border rounded text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="text"
              placeholder="Username"
              className="w-full mb-2 p-2 border rounded text-sm"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <input
              type="text"
              placeholder="Phone"
              className="w-full mb-3 p-2 border rounded text-sm"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="flex justify-between">
              <button
                className="bg-gray-600 text-white px-4 py-2 rounded text-sm"
                onClick={() => setShowPopup(false)}
              >
                Close
              </button>
              <button
                className="bg-gray-900 text-white px-4 py-2 rounded text-sm"
                onClick={handleLoginSignup}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-2 md:px-4 py-4">
        {searchResults.length > 0 && (
          <div className="mb-4 md:mb-8">
            <h2 className="text-lg md:text-2xl font-bold mb-2 md:mb-4">
              Search Results
            </h2>
            <div className="grid grid-cols-1 gap-2 md:gap-4 md:grid-cols-3 lg:grid-cols-4">
              {searchResults.map((product) => (
                <div
                  key={`${product.id}-${product.Category}`} // Added Category to key for uniqueness
                  className="border rounded p-2 md:p-4 cursor-pointer hover:shadow-lg"
                  onClick={() => handleViewProduct(product)}
                >
                  <img
                    src={product.ImageUrl1}
                    alt={product.ProductID}
                    className="w-full h-32 md:h-48 object-cover rounded mb-1 md:mb-2"
                  />
                  <p className="text-xs md:text-base font-semibold">
                    ID: {product.ProductID}
                  </p>
                  <p className="text-xs md:text-sm">
                    Category: {product.Category}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <ProductShowcase />

      {/* Animated Brand Scroller */}
      <div className="overflow-hidden bg-gray-800 py-2 md:py-4">
        <div className="animate-marquee whitespace-nowrap">
          {[...brands, ...brands].map((b, index) => (
            <div
              key={`brand-${index}`}
              className="inline-block mx-4 md:mx-8 text-lg md:text-xl font-semibold text-white"
            >
              {b}
            </div>
          ))}
        </div>
      </div>

      <TypesProducts />

      {/* Product Container */}
      <div className="w-11/12 mx-auto md:w-full">
        <Pcontainer />
        <Footer />
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
          display: inline-block;
        }
      `}</style>
    </div>
  );
}
